import { NSARouterMakerResult } from "namirasoft-account-react";
import { NamingConvention, SortItem } from "namirasoft-core";
import { ConditionGroupRow, ConditionRow, GuardRailRuleInputRow, GuardRailRuleType, GuardRailRuleTypeCustompromptInferencerType } from "namirasoft-inference";
import { NamirasoftMap } from "namirasoft-site-map";
import { IBaseComponentProps, NSBox, NSBoxBoolean, NSBoxEntity, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { InferenceRouterProps } from "../InferenceRouterProps";
import InferencerTypeID from "./InferencerTypeID";

export interface GuardRailRuleLineProps extends IBaseComponentProps, NSARouterMakerResult, InferenceRouterProps
{
    index: number;
    deletable: boolean;
    onDelete: () => void;
    onChanged?: (value: GuardRailRuleInputRow | null) => void;
}

interface GuardRailRuleLineState
{
    type: GuardRailRuleType | null;
}

export default class GuardRailRuleLine extends Component<GuardRailRuleLineProps, GuardRailRuleLineState>
{
    static IS_DIRTY(value: GuardRailRuleInputRow | null): boolean
    {
        if (value && value.type)
            return true;
        return false;
    }

    static typeRequiresInferencer(type: GuardRailRuleType): boolean
    {
        return type !== GuardRailRuleType.Condition && type !== GuardRailRuleType.ConditionGroup;
    }

    private NSBoxEnum_Type = createRef<NSBoxEnum<GuardRailRuleType>>();
    private NSBoxEntity_ConditionId = createRef<NSBoxEntity<ConditionRow>>();
    private NSBoxEntity_ConditionGroupId = createRef<NSBoxEntity<ConditionGroupRow>>();
    private NSBoxString_DeniedTopicTopics = createRef<NSBoxString>();
    private NSBoxString_PiiCustomSensitivePatternPattern = createRef<NSBoxString>();
    private NSBoxTextArea_CustomPromptPrompt = createRef<NSBoxTextArea>();
    private InferencerTypeID_CustomPromptInferencer = createRef<InferencerTypeID<GuardRailRuleTypeCustompromptInferencerType>>();
    private NSBoxBoolean_OnUserPrompt = createRef<NSBoxBoolean>();
    private NSBoxBoolean_OnAiOutput = createRef<NSBoxBoolean>();

    private pendingInferencer: { inferencer_type: GuardRailRuleTypeCustompromptInferencerType; inferencer_id: string } | null = null;

    constructor(props: GuardRailRuleLineProps)
    {
        super(props);
        this.state = { type: null };
        this.getValue = this.getValue.bind(this);
        this.isDirty = this.isDirty.bind(this);
        this.onChanged = this.onChanged.bind(this);
        this.onTypeChanged = this.onTypeChanged.bind(this);
    }

    private onTypeChanged()
    {
        let raw = this.NSBoxEnum_Type.current?.getValueEnumOne(GuardRailRuleType, null as unknown as GuardRailRuleType, false) as GuardRailRuleType | null | undefined;
        let next: GuardRailRuleType | null = raw ?? null;
        if (next !== this.state.type)
            this.setState({ type: next }, () => this.applyPendingInferencer());
        this.onChanged();
    }

    private applyPendingInferencer()
    {
        if (this.pendingInferencer && this.state.type && GuardRailRuleLine.typeRequiresInferencer(this.state.type))
        {
            this.InferencerTypeID_CustomPromptInferencer.current?.setValue(this.pendingInferencer);
            this.pendingInferencer = null;
        }
    }

    getValue(checkError: boolean = false): GuardRailRuleInputRow | null
    {
        let type = this.NSBoxEnum_Type.current?.getValueEnumOne(GuardRailRuleType, null as unknown as GuardRailRuleType, checkError);
        if (!type)
            return null;
        let on_user_prompt = this.NSBoxBoolean_OnUserPrompt.current?.getValue() ?? false;
        let on_ai_output = this.NSBoxBoolean_OnAiOutput.current?.getValue() ?? false;
        if (checkError && !on_user_prompt && !on_ai_output)
            throw new Error(`On User Prompt or On AI Output is required for Type ${this.props.index + 1}.`);

        let type_condition_condition_id: string | null = null;
        let type_conditiongroup_condition_group_id: string | null = null;
        let type_deniedtopic_topics: string | null = null;
        let type_piicustomsensitivepattern_pattern: string | null = null;
        let type_customprompt_prompt: string | null = null;
        let type_customprompt_inferencer_type: GuardRailRuleTypeCustompromptInferencerType | null = null;
        let type_customprompt_inferencer_id: string | null = null;

        if (type === GuardRailRuleType.Condition)
            type_condition_condition_id = this.NSBoxEntity_ConditionId.current?.getValueOne(checkError) ?? null;
        else if (type === GuardRailRuleType.ConditionGroup)
            type_conditiongroup_condition_group_id = this.NSBoxEntity_ConditionGroupId.current?.getValueOne(checkError) ?? null;
        else if (type === GuardRailRuleType.DeniedTopic)
            type_deniedtopic_topics = this.NSBoxString_DeniedTopicTopics.current?.getValue(checkError) || null;
        else if (type === GuardRailRuleType.PiiCustomSensitivePattern)
            type_piicustomsensitivepattern_pattern = this.NSBoxString_PiiCustomSensitivePatternPattern.current?.getValue(checkError) || null;
        else if (type === GuardRailRuleType.CustomPrompt)
            type_customprompt_prompt = this.NSBoxTextArea_CustomPromptPrompt.current?.getValue(checkError) || null;

        if (GuardRailRuleLine.typeRequiresInferencer(type))
        {
            let inferencer = this.InferencerTypeID_CustomPromptInferencer.current?.getValue(checkError);
            if (inferencer && inferencer.inferencer_id)
            {
                type_customprompt_inferencer_type = inferencer.inferencer_type;
                type_customprompt_inferencer_id = inferencer.inferencer_id;
            }
        }

        return {
            type,
            type_condition_condition_id,
            type_conditiongroup_condition_group_id,
            type_deniedtopic_topics,
            type_piicustomsensitivepattern_pattern,
            type_customprompt_prompt,
            type_customprompt_inferencer_type,
            type_customprompt_inferencer_id,
            on_user_prompt,
            on_ai_output,
        };
    }

    setValue(value: GuardRailRuleInputRow)
    {
        this.NSBoxEnum_Type.current?.setValue(value.type);
        this.NSBoxBoolean_OnUserPrompt.current?.setValue(value.on_user_prompt);
        this.NSBoxBoolean_OnAiOutput.current?.setValue(value.on_ai_output);

        if (GuardRailRuleLine.typeRequiresInferencer(value.type) && value.type_customprompt_inferencer_type && value.type_customprompt_inferencer_id)
        {
            this.pendingInferencer = {
                inferencer_type: value.type_customprompt_inferencer_type,
                inferencer_id: value.type_customprompt_inferencer_id,
            };
        }
        else
        {
            this.pendingInferencer = null;
        }

        this.setState({ type: value.type }, () =>
        {
            if (value.type === GuardRailRuleType.Condition)
                this.NSBoxEntity_ConditionId.current?.setValue(value.type_condition_condition_id);
            else if (value.type === GuardRailRuleType.ConditionGroup)
                this.NSBoxEntity_ConditionGroupId.current?.setValue(value.type_conditiongroup_condition_group_id);
            else if (value.type === GuardRailRuleType.DeniedTopic)
                this.NSBoxString_DeniedTopicTopics.current?.setValue(value.type_deniedtopic_topics);
            else if (value.type === GuardRailRuleType.PiiCustomSensitivePattern)
                this.NSBoxString_PiiCustomSensitivePatternPattern.current?.setValue(value.type_piicustomsensitivepattern_pattern);
            else if (value.type === GuardRailRuleType.CustomPrompt)
                this.NSBoxTextArea_CustomPromptPrompt.current?.setValue(value.type_customprompt_prompt);
            this.applyPendingInferencer();
        });
    }

    isDirty(): boolean
    {
        return GuardRailRuleLine.IS_DIRTY(this.getValue(false));
    }

    private onChanged()
    {
        if (this.props.onChanged)
            this.props.onChanged(this.getValue(false));
    }

    private renderTypeFields()
    {
        let type = this.state.type;
        if (!type)
            return null;
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        if (type === GuardRailRuleType.Condition)
        {
            return (
                <NSBoxEntity<ConditionRow>
                    ref={this.NSBoxEntity_ConditionId}
                    {...this.props}
                    title="Condition"
                    required
                    multiple={false}
                    info={{ link: "https://namirasoft.com/inference/console-guide/guard-rail/#field-rules" }}
                    table={namirasoft.inference.tables.condition}
                    getValue={(item) => item.id}
                    getTitle={(item) => item.name}
                    getSort={() => new SortItem(namirasoft.inference.tables.condition, namirasoft.inference.tables.condition.columns.name, true)}
                    onChanged={this.onChanged}
                    style={{ width: NSBox.width.double }}
                />
            );
        }
        if (type === GuardRailRuleType.ConditionGroup)
        {
            return (
                <NSBoxEntity<ConditionGroupRow>
                    ref={this.NSBoxEntity_ConditionGroupId}
                    {...this.props}
                    title="Condition Group"
                    required
                    multiple={false}
                    info={{ link: "https://namirasoft.com/inference/console-guide/guard-rail/#field-rules" }}
                    table={namirasoft.inference.tables.condition_group}
                    getValue={(item) => item.id}
                    getTitle={(item) => item.name}
                    getSort={() => new SortItem(namirasoft.inference.tables.condition_group, namirasoft.inference.tables.condition_group.columns.name, true)}
                    onChanged={this.onChanged}
                    style={{ width: NSBox.width.double }}
                />
            );
        }
        return (
            <NSColumn style={{ width: "100%", flex: 1 }}>
                <NSRow style={{ flexWrap: "nowrap", width: "100%" }}>
                    <InferencerTypeID<GuardRailRuleTypeCustompromptInferencerType>
                        ref={this.InferencerTypeID_CustomPromptInferencer}
                        {...this.props}
                        typeTitle="Inferencer Type"
                        idTitle="Inferencer ID"
                        required
                        info={{ link: "https://namirasoft.com/inference/console-guide/guard-rail/#field-rules" }}
                        fill
                        getEnumObject={() => GuardRailRuleTypeCustompromptInferencerType}
                        defaultType={GuardRailRuleTypeCustompromptInferencerType.MODEL}
                        onChanged={this.onChanged}
                    />
                </NSRow>
                {type === GuardRailRuleType.DeniedTopic && (
                    <NSBoxString
                        ref={this.NSBoxString_DeniedTopicTopics}
                        title="Topics (comma-separated)"
                        required
                        info={{ link: "https://namirasoft.com/inference/console-guide/guard-rail/#field-rules" }}
                        onChanged={this.onChanged}
                        style={{ width: "100%" }}
                    />
                )}
                {type === GuardRailRuleType.PiiCustomSensitivePattern && (
                    <NSBoxString
                        ref={this.NSBoxString_PiiCustomSensitivePatternPattern}
                        title="Pattern"
                        required
                        info={{ link: "https://namirasoft.com/inference/console-guide/guard-rail/#field-rules" }}
                        onChanged={this.onChanged}
                        style={{ width: "100%" }}
                    />
                )}
                {type === GuardRailRuleType.CustomPrompt && (
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_CustomPromptPrompt}
                        title="Prompt"
                        required
                        rows={6}
                        info={{ link: "https://namirasoft.com/inference/console-guide/guard-rail/#field-rules" }}
                        onChanged={this.onChanged}
                        style={{ width: "100%" }}
                    />
                )}
            </NSColumn>
        );
    }

    override render()
    {
        let typeFields = this.renderTypeFields();
        return (
            <NSColumn style={{ ...this.props.style }}>
                <NSRow style={{ flexWrap: "nowrap" }}>
                    <NSBoxEnum<GuardRailRuleType>
                        ref={this.NSBoxEnum_Type}
                        title={`Type ${this.props.index + 1}`}
                        required
                        multiple={false}
                        info={{ link: "https://namirasoft.com/inference/console-guide/guard-rail/#field-rules" }}
                        reloadable={false}
                        style={{ width: NSBox.width.double }}
                        getEnumObject={() => GuardRailRuleType}
                        getTitle={(value) => NamingConvention.auto.convert(value, NamingConvention.Pascal_Case_Space)}
                        onChanged={this.onTypeChanged}
                    />
                    <NSBoxBoolean
                        ref={this.NSBoxBoolean_OnUserPrompt}
                        title="On User Prompt"
                        required={false}
                        defaultValue={true}
                        info={{ link: "https://namirasoft.com/inference/console-guide/guard-rail/#field-rules" }}
                        onChanged={this.onChanged}
                    />
                    <NSBoxBoolean
                        ref={this.NSBoxBoolean_OnAiOutput}
                        title="On AI Output"
                        required={false}
                        info={{ link: "https://namirasoft.com/inference/console-guide/guard-rail/#field-rules" }}
                        onChanged={this.onChanged}
                        menu={{
                            builtin: { delete: { enabled: this.props.deletable, onDelete: () => { this.props.onDelete(); } } },
                            items: []
                        }}
                    />
                </NSRow>
                {typeFields && (
                    <NSRow style={{ flexWrap: "nowrap" }}>
                        {typeFields}
                    </NSRow>
                )}
            </NSColumn>
        );
    }
}